import React, { useEffect, useState } from "react";
import { CheckCircle, Loader2, AlertCircle, Lightbulb } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cultural facts to show during payment success
const culturalFacts = [
  {
    title: "Le premier DJ Hip Hop",
    content: "DJ Kool Herc, considéré comme le père du Hip Hop, a inventé le 'breakbeat' en 1973 lors d'une fête dans le Bronx."
  },
  {
    title: "Fela Kuti, le pionnier rebelle",
    content: "Fela Kuti a créé l'Afrobeat dans les années 70 en mélangeant le jazz, le funk et les rythmes yoruba."
  },
  {
    title: "Les racines du R&B",
    content: "Le terme 'Rhythm and Blues' a été créé en 1949 par Jerry Wexler, fusionnant blues, gospel et jazz."
  },
  {
    title: "Le Dancehall jamaïcain",
    content: "Le dancehall est né dans les années 70 dans les sound systems de Kingston, révolutionnant la musique caribéenne."
  }
];

export const PaymentSuccess = ({ onComplete }) => {
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [orderId, setOrderId] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [culturalFact] = useState(() => culturalFacts[Math.floor(Math.random() * culturalFacts.length)]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");

    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setOrderId(data.order_id);
        
        // Auto-redirect to confirmation page after 2 seconds
        setTimeout(() => {
          if (data.order_id && onComplete) {
            onComplete(data.order_id);
          }
        }, 2000);
        return;
      } else if (data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setPollCount(attempt + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-background/40 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
            <h2 className="text-2xl font-bold text-white">Vérification du paiement...</h2>
            <p className="text-muted-foreground">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
            <div className="flex justify-center gap-1 mt-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full ${i <= pollCount ? 'bg-primary' : 'bg-muted'}`}
                />
              ))}
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white">Paiement réussi !</h2>
            <p className="text-muted-foreground">
              Votre commande a été confirmée. Vous allez recevoir un email de confirmation avec votre billet.
            </p>
            {orderId && (
              <p className="text-sm text-muted-foreground">
                Numéro de commande: <span className="font-mono text-primary">{orderId.slice(0, 8)}</span>
              </p>
            )}
            
            {/* Le savais-tu? */}
            <div className="mt-6 p-4 bg-primary/10 border border-primary/30 rounded-lg text-left">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-primary font-semibold uppercase tracking-wide mb-1">Le savais-tu ?</p>
                  <p className="text-sm font-medium text-white mb-1">{culturalFact.title}</p>
                  <p className="text-xs text-muted-foreground">{culturalFact.content}</p>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground animate-pulse">
              Redirection automatique...
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white">Erreur de paiement</h2>
            <p className="text-muted-foreground">
              Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, veuillez nous contacter.
            </p>
            <Button
              onClick={() => window.location.href = "/"}
              className="mt-4"
            >
              Retour à l'accueil
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
